// vedaLabels.ts — single source of user-facing display strings for the Veda Agro
// "Deal Desk" trading cockpit. It ONLY changes presentation strings — never enum
// VALUES, route paths, request/response shapes, or math.
//
// Rule: every page title / section header / nav item / reply toast routes through
// VL[...] or vlabel(). Enum values stay; only the label the user reads is mapped here.
//
// Ported from design handoff veda-labels.jsx. ES exports — NO window assignment.

/** Relabel map: internal key → user-facing display string. */
export const VL = {
  // ── Navigation / page titles (RELABEL MAP) ──────────────────────
  sourcingWorkbench: 'Deal Desk',        // was "Sourcing Workbench"
  workPlan:          'Allocation Board',  // was "Work Plan"
  dailyRates:        'Rate Book',         // was "Daily Rates" / "Rate Update"
  triage:            'Routing',           // was "Triage"
  approvals:         'Margin Desk',       // was "Approvals"
  directory:         'Trade Network',     // was "Directory"
  enquiries:         'Requirements',      // was "Enquiries"
  orders:            'Contracts',         // was "Orders"
  dealOptions:       'Quotes',            // was "Deal Options"
  vendorGuard:       'Vendor Health',     // was vendor "Guard"

  // ── Terminology kept VERBATIM (do NOT relabel) ──────────────────
  margin:        'Margin',
  deliveredCost: 'Delivered Cost',
  freight:       'Freight',
} as const;

/** Keys available on the relabel map. */
export type VlKey = keyof typeof VL;

/** Params passed to a toast formatter. Loosely typed — each formatter reads
 *  only the fields it needs, all optional. */
export interface VedaToastParams {
  qty?: number;
  days?: number;
  vendor?: string;
  pct?: number;
  no?: string;
  city?: string;
}

/** Terse reply codes → full-sentence toasts. Codes that carry a value accept it:
 *  vtoast('MAP_DONE', { qty: 2000 }). */
export const VEDA_TOASTS: Record<string, (p?: VedaToastParams) => string> = {
  MAP_DONE:        (p) => `Allocated ${p?.qty != null ? p.qty.toLocaleString('en-IN') + ' kg' : 'quantity'}`,
  PO_NO_BALANCE:   () => 'PO has no balance left',
  RATE_STALE:      (p) => `Rate is ${p?.days ?? 'a few'} days old — refresh before quoting`,
  RATE_FLAGGED:    () => 'Rate flagged as suspect — needs a second source',
  QUOTE_SENT:      (p) => `Quote sent to ${p?.vendor ?? 'vendor'}`,
  MARGIN_LOW:      (p) => `Margin ${p?.pct != null ? p.pct + '%' : ''} is below the desk floor`,
  CONTRACT_BOOKED: (p) => `Contract ${p?.no ?? ''} booked`,
  CITY_PINNED:     (p) => `Pinned ${p?.city ?? 'location'} on the map`,
  APPROVED:        () => 'Approved and released to the Allocation Board',
};

/** Resolve a relabel key to its display string, with optional fallback. */
export function vlabel(key: string, fallback?: string): string {
  return (VL as Record<string, string>)[key] ?? fallback ?? key;
}

/** Resolve a terse reply code to a full-sentence toast. Never shows the raw
 *  code if mapped. */
export function vtoast(code: string, params?: VedaToastParams): string {
  const f = VEDA_TOASTS[code];
  return f ? f(params) : code;
}
